/**
 * React hooks for AI-powered interaction naming
 * Issue #362: Inline name editor in InteractionCatalog
 */

import { useState, useCallback, useRef } from 'react';

// Types
export interface NamingResult {
  suggestedName: string;
  confidence: number;
  reasoning?: string;
  source?: 'ai' | 'pattern';  // Indicates whether AI or pattern-based fallback was used
  usedDeepAnalysis?: boolean;  // Issue #385: Whether full file context was used
}

export interface NamingContext {
  handlerFunction: string;
  triggerElement: string;
  triggerType: string;
  componentFile: string;
  stateChanges?: string[];
  codeSnippet?: string;
  // Enhanced context for better naming
  screenName?: string;           // Inferred screen/page name
  interactionCategory?: string;  // toggle, navigation, modal-dialog, etc.
  flowDescription?: string;      // Flow description if available
  apiCalls?: string[];           // API endpoints called
  touchedTables?: string[];      // Database tables accessed
  touchedServices?: string[];    // External services called
  isCompleteFlow?: boolean;      // Whether it's a complete E2E flow
  flowSteps?: string[];          // Summary of flow steps for context
  // Component context (Issue #376)
  componentSummary?: string;     // Pre-generated component summary
  generateComponentSummary?: boolean;  // Request server to generate summary
  // Element context (Issue #378)
  elementText?: string;          // Button/link text content (e.g., "Cancel", "Delete")
  handlerName?: string;          // Handler function name from flow
  // Deep analysis (Issue #385)
  fullFileContent?: string;      // Full file content for deeper analysis
  useDeepAnalysis?: boolean;     // Enable deeper analysis mode
  readFullFile?: boolean;        // Request server to read full file
  // Per-interaction prompt customization (Issue #386)
  analysisDepth?: 'light' | 'standard' | 'deep';  // Controls context depth
  customGuidelines?: string;     // Additional guidelines (merged with project defaults)
  customSystemPrompt?: string;   // Override system prompt entirely
}

export interface ResolvedName {
  name: string;
  source: 'override' | 'ai' | 'pattern';
  reasoning?: string;
}

export interface AIStatus {
  available: boolean;
  source: 'user' | 'system' | 'none';
  provider: string | null;
}

// API functions
async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });
  return response;
}

/**
 * Hook for managing interaction name editing
 */
export function useInteractionName(projectId: string, interactionId: string) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedName, setResolvedName] = useState<ResolvedName | null>(null);

  // Get the current name
  const fetchName = useCallback(async (originalName?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = originalName ? `?originalName=${encodeURIComponent(originalName)}` : '';
      const response = await fetchWithAuth(
        `/api/projects/${projectId}/interactions/${interactionId}/name${params}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch name');
      }
      const data = await response.json();
      setResolvedName(data);
      return data as ResolvedName;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, interactionId]);

  // Override the name
  const overrideName = useCallback(async (name: string, originalName?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const params = originalName ? `?originalName=${encodeURIComponent(originalName)}` : '';
      const response = await fetchWithAuth(
        `/api/projects/${projectId}/interactions/${interactionId}/name${params}`,
        {
          method: 'PUT',
          body: JSON.stringify({ name }),
        }
      );
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save name');
      }
      const data = await response.json();
      setResolvedName({ name: data.name, source: data.source });
      return data;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, interactionId]);

  // Delete the override
  const deleteOverride = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(
        `/api/projects/${projectId}/interactions/${interactionId}/name`,
        { method: 'DELETE' }
      );
      if (!response.ok) {
        throw new Error('Failed to delete override');
      }
      setResolvedName(null);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [projectId, interactionId]);

  return {
    resolvedName,
    isLoading,
    error,
    fetchName,
    overrideName,
    deleteOverride,
    setResolvedName,
  };
}

/**
 * Hook for generating AI names
 */
export function useAINaming(projectId: string) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Generate AI name for a single interaction
  const generateName = useCallback(async (
    interactionId: string,
    context: NamingContext
  ): Promise<NamingResult | null> => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsGenerating(true);
    setError(null);
    try {
      const response = await fetchWithAuth(
        `/api/projects/${projectId}/interactions/${interactionId}/ai-name`,
        {
          method: 'POST',
          body: JSON.stringify(context),
          signal: abortControllerRef.current.signal,
        }
      );

      if (!response.ok) {
        const data = await response.json();
        if (data.code === 'AI_UNAVAILABLE') {
          throw new Error('AI service unavailable. Please add your API key in settings.');
        }
        if (data.code === 'RATE_LIMITED') {
          throw new Error('Too many requests. Please try again later.');
        }
        throw new Error(data.error || 'Failed to generate name');
      }

      const data = await response.json();
      return {
        suggestedName: data.suggestedName,
        confidence: data.confidence,
        reasoning: data.reasoning,
        source: data.source,
        usedDeepAnalysis: data.usedDeepAnalysis,  // Issue #385
      };
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return null; // Request was cancelled
      }
      setError(err instanceof Error ? err.message : 'Unknown error');
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, [projectId]);

  // Cancel pending request
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsGenerating(false);
  }, []);

  return {
    isGenerating,
    error,
    generateName,
    cancel,
    clearError: () => setError(null),
  };
}

/**
 * Hook for submitting feedback on AI names
 */
export function useNameFeedback(projectId: string) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitFeedback = useCallback(async (
    interactionId: string,
    feedback: {
      aiSuggestedName: string;
      aiConfidence?: number;
      feedbackType: 'good' | 'bad' | 'edited';
      userProvidedName?: string;
      handlerName?: string;
      triggerElement?: string;
      componentFile?: string;
      codeSnippet?: string;
      reasoning?: string;
    },
    originalName?: string
  ) => {
    setIsSubmitting(true);
    setError(null);
    try {
      const params = originalName ? `?originalName=${encodeURIComponent(originalName)}` : '';
      const response = await fetchWithAuth(
        `/api/projects/${projectId}/interactions/${interactionId}/name-feedback${params}`,
        {
          method: 'POST',
          body: JSON.stringify(feedback),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to submit feedback');
      }

      return await response.json();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return null;
    } finally {
      setIsSubmitting(false);
    }
  }, [projectId]);

  return {
    isSubmitting,
    error,
    submitFeedback,
    clearError: () => setError(null),
  };
}

/**
 * Hook to check AI availability
 */
export function useAIStatus(projectId: string) {
  const [status, setStatus] = useState<AIStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const checkStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `/api/projects/${projectId}/ai-status`
      );
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
        return data as AIStatus;
      }
      return null;
    } catch {
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  return {
    status,
    isLoading,
    checkStatus,
  };
}

/**
 * Hook to get all resolved names for a project (bulk)
 */
export function useResolvedNames(projectId: string) {
  const [names, setNames] = useState<Record<string, ResolvedName>>({});
  const [isLoading, setIsLoading] = useState(false);
  // Version counter to force re-renders when names change
  const [version, setVersion] = useState(0);

  const fetchAllNames = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `/api/projects/${projectId}/interactions/names`
      );
      if (response.ok) {
        const data = await response.json();
        setNames(data.names || {});
        setVersion(v => v + 1);
        return data.names;
      }
      return {};
    } catch {
      return {};
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  // Update a single name locally
  const updateLocalName = useCallback((interactionId: string, name: string, source: 'override' | 'ai' | 'pattern', reasoning?: string) => {
    setNames(prev => ({
      ...prev,
      [interactionId]: { name, source, reasoning },
    }));
    // Increment version to ensure components re-render
    setVersion(v => v + 1);
  }, []);

  return {
    names,
    isLoading,
    fetchAllNames,
    updateLocalName,
    version,  // Expose version for components that need to track changes
  };
}
